import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckIcon, InfoIcon } from 'lucide-react';
import { Product, ProductOption, CartItemType } from '../types/types';
import { BOWL_OPTIONS } from '../data/menuData';
import { CONFIG } from '../data/config';
interface ProductCustomizerProps {
  product: Product;
  onClose: () => void;
  onSave: (item: CartItemType) => void;
  initialItem?: CartItemType;
}
export function ProductCustomizer({
  product,
  onClose,
  onSave,
  initialItem
}: ProductCustomizerProps) {
  const isSandwichOrWrap = product.type === 'sandwich' || product.type === 'wrap';
  const isSnackBowl = product.type === 'snack_bowl';
  const [selections, setSelections] = useState<any>(initialItem?.selections || {
    toppings: [],
    aderezos: [],
    comentarios: ''
  });
  const handleToppingSelect = (option: ProductOption) => {
    const currentList = selections.toppings || [];
    const exists = currentList.find((item: ProductOption) => item.id === option.id);
    if (exists) {
      setSelections({
        ...selections,
        toppings: currentList.filter((item: ProductOption) => item.id !== option.id)
      });
    } else {
      if (isSandwichOrWrap && currentList.length >= 4) return;
      setSelections({
        ...selections,
        toppings: [...currentList, option]
      });
    }
  };
  const handleAderezoSelect = (option: ProductOption) => {
    const currentList = selections.aderezos || [];
    const exists = currentList.find((item: ProductOption) => item.id === option.id);
    if (exists) {
      setSelections({
        ...selections,
        aderezos: currentList.filter((item: ProductOption) => item.id !== option.id)
      });
    } else {
      if (isSandwichOrWrap && currentList.length >= 1) return;
      setSelections({
        ...selections,
        aderezos: [...currentList, option]
      });
    }
  };
  const calculateSubtotal = () => {
    let total = product.price;
    return total;
  };
  const handleSave = () => {
    const item: CartItemType = {
      id: initialItem?.id || `${product.id}_${Date.now()}`,
      productId: product.id,
      name: product.name,
      basePrice: product.price,
      quantity: initialItem?.quantity || 1,
      selections,
      subtotal: calculateSubtotal(),
      type: product.type || 'bowl'
    };
    onSave(item);
  };
  // Allow 1-4 toppings (at least 1 required) and 0-1 aderezo (optional)
  const isSaveValid = () => {
    if (isSandwichOrWrap) {
      return (selections.toppings?.length || 0) >= 1;
    }
    return true;
  };
  const toppingCount = selections.toppings?.length || 0;
  const aderezoCount = selections.aderezos?.length || 0;
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 50
  }} className="fixed inset-0 z-50 bg-background flex flex-col max-w-md mx-auto shadow-2xl" data-id="element-311">
      <div className="bg-white px-4 py-4 flex items-center justify-between shadow-sm z-10" data-id="element-312">
        <button onClick={onClose} className="p-2 -ml-2 text-gray-500 hover:text-gray-900" data-id="element-313">
          <XIcon className="w-6 h-6" data-id="element-314" />
        </button>
        <h2 className="font-heading font-semibold text-lg" data-id="element-315">Personalizar</h2>
        <div className="w-10" data-id="element-316"></div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-24" data-id="element-317">
        <div className="mb-6" data-id="element-318">
          <h3 className="text-2xl font-heading font-bold text-gray-900 mb-1" data-id="element-319">
            {product.name}
          </h3>
          <p className="text-gray-500 text-sm" data-id="element-320">{product.description}</p>
          <p className="text-verde-dark font-semibold mt-2" data-id="element-321">
            {CONFIG.CURRENCY_SYMBOL}
            {product.price.toLocaleString()}
          </p>
        </div>

        {isSandwichOrWrap && <>
            {/* Toppings Section */}
            <div className="mb-8" data-id="element-322">
              <div className="flex justify-between items-end mb-1" data-id="element-323">
                <h4 className="font-heading font-semibold text-lg" data-id="element-324">Toppings</h4>
                <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${toppingCount >= 1 ? 'bg-verde/10 text-verde-dark' : 'bg-gray-100 text-gray-500'}`} data-id="element-325">
                  {toppingCount}/4
                </span>
              </div>
              <p className="text-gray-400 text-xs mb-3" data-id="element-326">
                Elegí de 1 a 4 toppings — mínimo 1 para continuar
              </p>

              {toppingCount === 4 && <p className="text-xs text-verde-dark bg-verde/10 px-3 py-1.5 rounded-lg mb-3 flex items-center gap-1" data-id="element-327">
                  <CheckIcon className="w-3 h-3" data-id="element-328" /> ¡Máximo alcanzado! Tocá uno
                  para cambiarlo
                </p>}

              <div className="grid grid-cols-2 gap-2" data-id="element-329">
                {BOWL_OPTIONS.toppings.map(option => {
              const isSelected = selections.toppings?.some((t: ProductOption) => t.id === option.id);
              const isDisabled = !isSelected && toppingCount >= 4;
              return <button key={option.id} onClick={() => handleToppingSelect(option)} disabled={isDisabled} className={`p-3 rounded-xl border text-sm text-left transition-colors ${isSelected ? 'border-verde bg-verde/10 text-verde-dark font-medium' : isDisabled ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed' : 'border-gray-200 bg-white hover:border-verde/50'}`} data-id="element-330">
                      {option.name}
                    </button>;
            })}
              </div>
            </div>

            {/* Aderezos Section */}
            <div className="mb-8" data-id="element-331">
              <div className="flex justify-between items-end mb-1" data-id="element-332">
                <div className="flex items-center gap-2" data-id="element-333">
                  <h4 className="font-heading font-semibold text-lg" data-id="element-334">
                    Aderezo
                  </h4>
                  <span className="text-xs font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full" data-id="element-335">
                    Opcional
                  </span>
                </div>
                <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${aderezoCount >= 1 ? 'bg-verde/10 text-verde-dark' : 'bg-gray-100 text-gray-500'}`} data-id="element-336">
                  {aderezoCount}/1
                </span>
              </div>
              <p className="text-gray-400 text-xs mb-3" data-id="element-337">
                Elegí 1 aderezo o dejalo vacío si preferís sin aderezo
              </p>

              <div className="grid grid-cols-2 gap-2" data-id="element-338">
                {BOWL_OPTIONS.aderezos.map(option => {
              const isSelected = selections.aderezos?.some((a: ProductOption) => a.id === option.id);
              const isDisabled = !isSelected && aderezoCount >= 1;
              return <button key={option.id} onClick={() => handleAderezoSelect(option)} disabled={isDisabled} className={`p-3 rounded-xl border text-sm text-left transition-colors ${isSelected ? 'border-verde bg-verde/10 text-verde-dark font-medium' : isDisabled ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed' : 'border-gray-200 bg-white hover:border-verde/50'}`} data-id="element-339">
                      {option.name}
                    </button>;
            })}
              </div>
            </div>
          </>}

        {/* Comments Section */}
        <div className="mb-8" data-id="element-340">
          <div className="flex items-center gap-2 mb-1" data-id="element-341">
            <h4 className="font-heading font-semibold text-lg" data-id="element-342">Comentarios</h4>
            <span className="text-xs font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full" data-id="element-343">
              Opcional
            </span>
          </div>
          <p className="text-gray-400 text-xs mb-3" data-id="element-344">
            ¿Alguna instrucción especial? Si no, simplemente agregá al pedido
          </p>
          <textarea value={selections.comentarios} onChange={e => setSelections({
          ...selections,
          comentarios: e.target.value
        })} placeholder="Ej: sin cebolla, extra salsa, etc." className="w-full h-24 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-verde focus:border-transparent resize-none" data-id="element-345" />
        </div>
      </div>

      <div className="bg-white border-t border-gray-100 p-4 pb-safe sticky bottom-0" data-id="element-346">
        {!isSaveValid() && isSandwichOrWrap && <p className="text-center text-xs text-terracota mb-2 flex items-center justify-center gap-1" data-id="element-347">
            <InfoIcon className="w-3 h-3" data-id="element-348" />
            Elegí al menos 1 topping para continuar
          </p>}
        <button onClick={handleSave} disabled={!isSaveValid()} className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors min-h-[48px] ${isSaveValid() ? 'bg-terracota text-white active:bg-[#9a3a18] shadow-md shadow-terracota/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-349">
          Agregar al pedido — {CONFIG.CURRENCY_SYMBOL}
          {calculateSubtotal().toLocaleString()}
        </button>
      </div>
    </motion.div>;
}